import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ArrowLeft, UploadSimple, CloudArrowUp, Trash, DeviceMobile } from "@phosphor-icons/react";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AdminOta() {
  const { user, loading } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [devices, setDevices] = useState(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, d] = await Promise.all([api.get("/ota/admin/bundles"), api.get("/ota/admin/devices")]);
      setBundles(b.data);
      setDevices(d.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const upload = async () => {
    if (!version.trim() || !file) return toast.error("Version and zip file are required");
    const fd = new FormData();
    fd.append("version", version.trim());
    fd.append("notes", notes.trim());
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/ota/admin/bundles", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bundle ${version.trim()} uploaded (unpublished)`);
      setVersion(""); setNotes(""); setFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setPublished = async (v, pub) => {
    try {
      await api.post(`/ota/admin/bundles/${v}/${pub ? "publish" : "unpublish"}`);
      toast.success(`${v} ${pub ? "published — devices will pick it up on next launch" : "unpublished"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete bundle ${v}?`)) return;
    try {
      await api.delete(`/ota/admin/bundles/${v}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="admin-ota-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="ota-back-link">
        <ArrowLeft size={16} /> Admin
      </Link>
      <h1 className="text-2xl font-bold mb-1">OTA Live Updates</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Push web bundle updates straight to installed iOS &amp; Android apps — no store review.
        Requires the v1.4+ native binary. OTA changes JS/assets only; native plugin changes still need a store release.
      </p>

      {devices && (
        <div className="rounded-xl border bg-card p-4 mb-6" data-testid="ota-device-stats">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2"><DeviceMobile size={16} /> Devices checking in: {devices.total}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(devices.by_version).map(([v, n]) => (
              <span key={v} className="text-xs bg-muted rounded-full px-2.5 py-1">{v}: {n}</span>
            ))}
            {devices.total === 0 && <span className="text-xs text-muted-foreground">None yet — devices appear here once a v1.4+ build launches.</span>}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-card p-4 mb-8">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><UploadSimple size={16} /> Upload new bundle</div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Input placeholder="Version (e.g. 1.4.1 — must be higher than devices)" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="ota-version-input" />
          <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ota-notes-input" />
        </div>
        <div className="flex items-center gap-3">
          <input type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="ota-file-input" />
          <Button onClick={upload} disabled={busy} data-testid="ota-upload-btn">{busy ? "Uploading…" : "Upload"}</Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Zip of the production build with index.html at the root. Uploads start unpublished — publish when ready.</p>
      </div>

      <div className="space-y-3" data-testid="ota-bundle-list">
        {bundles.map((b) => (
          <div key={b.version} className="rounded-xl border bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`ota-bundle-${b.version}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold flex items-center gap-2">
                v{b.version}
                {b.is_published
                  ? <span className="text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-700 rounded-full px-2 py-0.5">Live</span>
                  : <span className="text-[10px] font-bold uppercase tracking-wide bg-muted text-muted-foreground rounded-full px-2 py-0.5">Draft</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {fmtSize(b.size)} · {fmtDate(b.created_at)}{b.notes ? ` · ${b.notes}` : ""}
              </div>
              <div className="text-[10px] font-mono text-muted-foreground/70 mt-0.5 truncate">sha256 {b.checksum}</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={b.is_published ? "outline" : "default"} onClick={() => setPublished(b.version, !b.is_published)} data-testid={`ota-toggle-${b.version}`}>
                <CloudArrowUp size={15} className="mr-1" /> {b.is_published ? "Unpublish" : "Publish"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(b.version)} data-testid={`ota-delete-${b.version}`}>
                <Trash size={15} />
              </Button>
            </div>
          </div>
        ))}
        {bundles.length === 0 && <p className="text-sm text-muted-foreground">No bundles yet.</p>}
      </div>
    </div>
  );
}
